import { covertToArray, toggleArrayValue } from "~/lib/helpers";
import { useOnObjectChanged } from "~/lib/useOnObjectChanged";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { Dispatch, useMemo, useReducer } from "react";
import { PaginatedResponse } from "..";
import { ByDistanceQuery, PaginatedQueryParams } from "../types";
import { ImageMedia, Media } from "./Media";
import { OperatingHour } from "./OperatingHours";
import { BasicRecord, RecordListItem } from "./Records";
import { Social } from "./Socials";
import { Tag, TagFilterParams } from "./Tags";
import { useAddUserLocationToQuery } from "./locateable";
import {
  fetchPaginatedQueryFunction,
  fetchQueryFunction,
  makeQueryKey,
  PaginatedQueryFunction,
  useInfiniteQuery,
  UseInfiniteQueryFunction,
  usePaginatedQuery,
  UsePaginatedQueryFunction,
} from "./queries";

export type ListingContestingExtension = {
  redeemable_location?: boolean;
  redemption_amount?: number;
  redemption_code?: string;
  custom_text?: string;
};

export type ListingSortOptionKeys =
  | "featured"
  | "popular"
  | "alpha"
  | "distance";

/** Values accepted by the public listings API `sort` param (incl. CMS `sort_by`). */
export type ListingSortParam =
  | ListingSortOptionKeys
  | "random"
  | "newest"
  | "oldest"
  | "created_at"
  | "updated_at";

export const ListingSortOptions: { [key in ListingSortOptionKeys]: string } = {
  featured: "Featured",
  popular: "Most Popular",
  alpha: "Name (A-Z)",
  distance: "Distance (nearest first)",
};

export type ListingFilter = {
  featured?: boolean;
  main_type?: string | string[];
  region_id?: number | number[];
  tags?: TagFilterParams;
  priority_tag?: TagFilterParams;
  checkinable?: boolean;
  open_now?: boolean;
  city?: string[];
  extension_data?: { contesting_extension: { redeemable_location: boolean } };
};

export type ListingQueryParams = ByDistanceQuery &
  PaginatedQueryParams & {
    sort?: ListingSortParam;
    search?: string;
    filter?: ListingFilter;
    excluded_id?: number | number[];
    and?: ListingFilter;
  };

/**
 * `GET /listings/tags` and `GET /listings/attribute_counts` scope counts by filters
 * only. Passing `sort` (and `page`) from the main list query is serialized as query
 * params (`?sort=…`) and can trigger a 500 on those endpoints.
 */
export function toListingsFacetParams(
  params: ListingQueryParams = {},
): ListingQueryParams {
  const { sort, page, ...rest } = params;
  return rest;
}

export type Listing = BasicRecord & {
  uuid: string;
  slug: string;
  location_id: number;
  full_address?: string;
  street_address?: string;
  city?: string;
  province?: string;
  email?: string;
  timezone?: string;
  phone_number?: string;
  website?: string;
  booking_website?: string;
  description?: string;
  short_description?: string;
  main_type: string;
  socials: Social[];
  album_media: Media[];
  lead_media?: Media;
  thumbnail_media?: ImageMedia;
  tags: Tag[];
  operating_hours: OperatingHour[];
  region_name?: string;
  extension_data?: { contesting_extension?: ListingContestingExtension };
};
export const fetchListingList = (params?: ListingQueryParams) =>
  fetchQueryFunction<ListingQueryParams, RecordListItem[]>({
    path: "listings/list",
    resource: "listing-list",
    params,
  });

export const useListingList = (default_params?: ListingQueryParams) => {
  const { params, enabled } = useAddUserLocationToQuery(default_params);
  return useQuery<RecordListItem[], Error>({
    queryKey: makeQueryKey({ resource: "listing-list", params }),
    queryFn: () => fetchListingList(params),
    enabled,
  });
};

export const fetchListing = (id?: number | string) =>
  fetchQueryFunction<void, Listing>({
    path: `listings/${id}`,
    resource: "listings",
    id,
  });

export const useListing = (
  id?: number,
  options?: Omit<UseQueryOptions<Listing, Error>, "queryKey" | "queryFn">,
) =>
  useQuery<Listing, Error>({
    queryKey: makeQueryKey({ resource: "listings", id }),
    queryFn: () => fetchListing(id),
    ...options,
    enabled: !!id && options?.enabled,
  });

export type ListingResponse = PaginatedResponse<Listing>;

export const fetchListings: PaginatedQueryFunction<
  ListingQueryParams,
  ListingResponse
> = (params, preload) =>
  fetchPaginatedQueryFunction({
    path: "listings",
    resource: "listings",
    params,
    preload,
  });

/** `GET /listings/by_type` — listings grouped by `main_type`. */
export type ListingsByTypeQueryParams = {
  filter?: ListingFilter;
  and?: ListingFilter;
  name?: string;
  order?: string;
  limit?: number;
  not_id?: number | number[];
};

export type ListingsByTypeResponse = Record<string, Listing[]>;

export const fetchListingsByType = (params?: ListingsByTypeQueryParams) =>
  fetchQueryFunction<ListingsByTypeQueryParams, ListingsByTypeResponse>({
    path: "listings/by_type",
    resource: ["listings", "by-type"],
    params,
  });

export const flattenListingsByType = (grouped: ListingsByTypeResponse) =>
  Object.values(grouped)
    .flat()
    .filter((listing) => listing.lonlat);

export const useListingsByType = (
  params?: ListingsByTypeQueryParams,
  options?: Omit<
    UseQueryOptions<ListingsByTypeResponse, Error>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery<ListingsByTypeResponse, Error>({
    queryKey: makeQueryKey({ resource: ["listings", "by-type"], params }),
    queryFn: () => fetchListingsByType(params),
    ...options,
  });

export const useMapListings = (
  params?: ListingsByTypeQueryParams,
  options?: Omit<
    UseQueryOptions<ListingsByTypeResponse, Error>,
    "queryKey" | "queryFn"
  >,
) => {
  const query = useListingsByType(params, options);
  const listings = useMemo(
    () => (query.data ? flattenListingsByType(query.data) : []),
    [query.data],
  );
  return { ...query, listings };
};

export const fetchListingsTags = (params?: ListingQueryParams) =>
  fetchQueryFunction<ListingQueryParams, Tag[]>({
    path: "listings/tags",
    resource: ["listings", "tags"],
    params: toListingsFacetParams(params),
  });

export const useListings: UsePaginatedQueryFunction<
  Listing,
  ListingQueryParams
> = (params, options) =>
  usePaginatedQuery("listings", fetchListings, params, options);

export const useInfiniteListings: UseInfiniteQueryFunction<
  Listing,
  ListingQueryParams
> = (params, options) =>
  useInfiniteQuery("listings", fetchListings, params, options);

export type ListingFilterAction =
  | { type: "toggleCity"; payload: string }
  | { type: "toggleTagId"; payload: number }
  | { type: "setRegionId"; payload: number }
  | { type: "clearValue"; payload: "city" | "region_id" | "tags" | "main_type" }
  | { type: "setSearch"; payload?: string }
  | { type: "setTagIds"; payload?: number[] }
  | { type: "setMainTypes"; payload?: string }
  | { type: "reset"; payload?: ListingQueryParams };

export type ListingFilterReducer = Dispatch<NonNullable<ListingFilterAction>>;

export const useListingFilterParams = (
  defaultParams: ListingQueryParams = {},
) => {
  const reducer = useReducer(
    (
      state: ListingQueryParams,
      { type, payload }: NonNullable<ListingFilterAction>,
    ) => {
      // const { type, payload } = action;
      switch (type) {
        case "toggleCity":
          const currentCity = state.filter?.city ?? [];

          return {
            ...state,
            filter: {
              ...state.filter,
              city: toggleArrayValue(currentCity, payload),
            },
          };
        case "setRegionId":
          const currentRegionId = covertToArray(state.filter?.region_id);
          return {
            ...state,
            filter: {
              ...state.filter,
              region_id: toggleArrayValue(currentRegionId, payload),
            },
          };
        case "setMainTypes":
          if (!payload) return state;
          const currentMainTypes = covertToArray(state.filter?.main_type);
          return {
            ...state,
            filter: {
              ...state.filter,
              main_type: toggleArrayValue(currentMainTypes, payload),
            },
          };

        case "toggleTagId":
          const currentTagId = covertToArray(state.filter?.tags?.id);

          return {
            ...state,
            filter: {
              ...state.filter,
              tags: { id: toggleArrayValue(currentTagId, payload) },
            },
          };
        case "setTagIds":
          return {
            ...state,
            filter: {
              ...state.filter,
              tags: {
                ...defaultParams.filter?.tags,
                id: [
                  ...covertToArray(defaultParams.filter?.tags?.id),
                  ...covertToArray(payload),
                ],
              },
            },
          };
        case "clearValue":
          if (!state.filter) return { ...state };
          const { [payload]: existingValue, ...newFilter } = state.filter;
          return {
            ...state,
            filter: { ...newFilter, ...defaultParams.filter },
          };
        case "setSearch":
          return { ...state, search: payload };
        case "reset":
          return { ...payload };
        default:
          return { ...state };
      }
    },
    defaultParams,
  );

  // When navigating to a new page in the same route, reset the filter params.
  useOnObjectChanged(defaultParams, (params) => {
    reducer[1]({ type: "reset", payload: params });
  });
  return reducer;
};
